import { reactive, toRaw } from "vue";
import { Facade, DraggerType } from "@/drawing";
import { eraserConfig } from "@/drawing/draggers/eraserConfig";
import { selectConfig } from "@/drawing/draggers/selectConfig";
import NoteMenuState from "./NoteMenuState";
import type { StoredSettings } from "./CanvasSettingsState";
import _ from "lodash";
import CanvasSettingsState from "./CanvasSettingsState";
import Settings from "@/stores/Settings";
import { Logger } from "@/logger";
import { DEFAULT_LEGEND_SETTINGS } from "@/defaultLegend";
import type {
  PhotoMarkerShape,
  PhotoMarkerMode,
  PhotoMarkerAttribute,
} from "@/drawing";
import { LoadingTimer } from "@/utils/LoadingTimer";
import { toRulerSize } from "@/drawing/overlay/rulerSizes";
import type { Layer } from "@/types";
import { findDraggerType } from "@/drawing/draggers/types";
import {
  trackAssist,
  trackShapeChange,
  trackTab,
  trackTool,
  isRestoringActive,
  withRestoreSuppressed,
  type ToolSource,
} from "@/analytics/drawing-analytics";
import { trackDebounced } from "@/analytics/track-debounce";
import { HANDWRITING_TOOL_ENABLED } from "@/services/handwriting/featureFlag";
import { OBJECT_SNAP_MODE_ORDER } from "@/drawing/snap";
import type { ObjectSnapMode } from "@/drawing/snap";
import { DEFAULT_OBJECT_SNAP_MODES } from "@/drawing/snap";

// Types for dragger configuration
export type DraggerConfig = {
  name: DraggerType;
};

// Polar angle modes for snap
export enum PolarModes {
  polar90 = "90",
  polar45 = "45",
  polar30 = "30",
}
export type PolarAngleMode = "90" | "45" | "30";

// Map [displayed slider value, actual smoothen value]
//
// The right-hand value is the tolerance handed to simplifyPolyline(), in
// screen pixels. What the tool actually does is DROP vertices: users want few
// points on a freehand stroke, so that selecting and dragging a point
// afterwards stays precise. The slider is therefore a "how few points do I
// want" control.
//
// The catch is that Ramer-Douglas-Peucker collapses off a cliff, and the cliff
// sits wherever the user's hand tremor sits — not where the setting sits.
// Vertices left on a 900 px crack outline, measured on simulated strokes:
//
//   tolerance          1 px   1.5    2     2.5    3     4     8
//   very steady stylus   39    25    24     21    17    14     9
//   normal stylus       327   221   107     34    19    16    11
//   shaky hand         1816  1517  1077    663   434   186    11
//
// The previous scale (3 px .. 40 px, default 8 px) put every user past the
// cliff: about ten vertices whatever they did, and no visible difference from
// 40 % of the slider upwards — three quarters of its travel did nothing.
// Everything worth reaching happens between 1.5 px and 4 px, so that is what
// the slider now spends its travel on, and it stops at 10 px because beyond
// that RDP has nothing left to remove.
//
// The keys are unchanged, so a stored setting keeps its slider position; the
// default (30) now leaves roughly three times more points than it used to.
const SMOOTHEN_VALUES = new Map<number, number>([
  [0, 0],
  [10, 1.5],
  [20, 2],
  [30, 2.5],
  [40, 3],
  [50, 3.5],
  [60, 4],
  [70, 5],
  [80, 6],
  [90, 8],
  [100, 10],
]);

const defaultSmoothenIndex: number = 30;

const HANDLES_SCALE_VALUES = new Map<number, number>([
  [0, 0.2],
  [10, 0.4],
  [20, 0.6],
  [30, 0.8],
  [40, 1.0],
  [50, 1.2],
  [60, 1.4],
  [70, 1.6],
  [80, 1.8],
  [90, 2],
  [100, 2.2],
]);

export const defaultHandleScaleIndex = 30;

/** Facteur de taille des poignées (sélection, règles) pour un cran du réglage. */
export function handlesScaleFactor(index: number): number {
  return (
    HANDLES_SCALE_VALUES.get(index) ??
    HANDLES_SCALE_VALUES.get(defaultHandleScaleIndex)!
  );
}

// Map [displayed slider value (0-100, step 10), actual aperture in px]
const ERASER_SIZE_VALUES = new Map<number, number>([
  [0, 5],
  [10, 8],
  [20, 12],
  [30, 16],
  [40, 20],
  [50, 25],
  [60, 30],
  [70, 35],
  [80, 40],
  [90, 45],
  [100, 50],
]);

const defaultEraserSizeIndex = 40; // 20px aperture

type DraggerMenuState = {
  activeDragger: DraggerConfig;
  activePhotoMarkerShape: PhotoMarkerShape;
  activePhotoMarkerMode: PhotoMarkerMode;
  activeArrowType: "thin_arrow" | "large_arrow";
  isLwActive: boolean;
  label: boolean;
  orthoMode: boolean;
  polarMode: boolean;
  polarAngleMode: PolarAngleMode;
  objectSnapEnabled: boolean;
  objectSnapModes: Set<ObjectSnapMode>;
  storedSettings: StoredSettings | null;
  origin: [number, number, number];
  closeContour: boolean;
  sequentialPointMode: boolean;
  smoothen: number;
  handlesScale: number;
  eraserSize: number;
  additiveSelection: boolean;
  rulerAnchor: { hY: number; vX: number } | null;
  lockNativeElements: boolean;
  rulerVisible: boolean;
  initialize: (storedSettings: StoredSettings | null) => void;
  onDrawingOpen: (options?: { skipLineweight?: boolean }) => void;
  /** Body of `onDrawingOpen`. Call that instead — it suppresses tracking. */
  restoreOnDrawingOpen: (options?: { skipLineweight?: boolean }) => void;
  applyLineweight: () => void;
  setActiveDragger: (dragger: DraggerConfig, source?: ToolSource) => void;
  setActiveText: (text: string) => void;
  setPhotoMarkerShapeFromFavorite: (shape: PhotoMarkerShape) => void;
  persistPhotoMarker: () => void;
  persistOrigin: (coord: [number, number, number]) => void;
  toggleCloseContour: () => void;
  toggleSequentialPointMode: () => void;
  toggleLabel: () => void;
  setSmoothen: (value: number) => void;
  setHandlesScale: (value: number) => void;
  setEraserSize: (value: number) => void;
  setAdditiveSelection: (enabled: boolean) => void;
  toggleLineWeight: () => void;
  toggleLockNativeElements: () => void;
  setOrthoMode: (isOrtho: boolean) => void;
  setPolarMode: (isPolar: boolean) => void;
  setPolarAngleMode: (mode: PolarAngleMode) => void;
  setObjectSnapEnabled: (isEnabled: boolean) => void;
  toggleObjectSnap: () => void;
  toggleObjectSnapMode: (mode: ObjectSnapMode) => void;
  isObjectSnapModeEnabled: (mode: ObjectSnapMode) => boolean;
  getFavoritePhotoMarkers: () => PhotoMarkerShape[];
  getPhotoMarkerAttribute: () => PhotoMarkerAttribute;
  getPhotoMarkerDateFormat: () => string;
  syncPhotoMarkerAttributeConfig: () => void;
  getPhotoMarkerMode: () => PhotoMarkerMode;
  setPhotoMarkerMode: (mode: PhotoMarkerMode) => void;
  ensureValidPhotoMarkerShape: () => void;
  getPhotoLayer: () => Layer | undefined;
  getLayer: (layerName: string) => Layer | undefined;
  getLayerById: (layerId: string) => Layer | undefined;
  storeSnap: () => void;
};

export default reactive<DraggerMenuState>({
  activePhotoMarkerShape: "circle",
  activePhotoMarkerMode: "entity" as PhotoMarkerMode,
  activeArrowType: "thin_arrow",
  label: true,
  orthoMode: false,
  polarMode: false,
  polarAngleMode: PolarModes.polar45,
  objectSnapEnabled: false,
  objectSnapModes: new Set<ObjectSnapMode>(),
  activeDragger: { name: DraggerType.None },
  origin: [0, 0, 0],
  isLwActive: true,
  storedSettings: null,
  closeContour: true,
  sequentialPointMode: false,
  smoothen: defaultSmoothenIndex,
  handlesScale: defaultHandleScaleIndex,
  eraserSize: defaultEraserSizeIndex,
  additiveSelection: false,
  rulerAnchor: null,
  lockNativeElements: false,
  rulerVisible: true,
  initialize(storedSettings: StoredSettings | null) {
    this.storedSettings = storedSettings;
    if (storedSettings?.dragger?.name) {
      const stored = findDraggerType(storedSettings?.dragger?.name);
      // A device that was left on the freehand tool before it was switched
      // off would silently restore it — an active tool with no button, and
      // a model download on the first stroke. Fall back to plain text.
      this.activeDragger.name =
        !HANDWRITING_TOOL_ENABLED && stored === DraggerType.Handwriting
          ? DraggerType.Text
          : stored;
    } else {
      this.activeDragger.name = DraggerType.None;
    }

    // Load photo marker shape - convert legacy "arrow" format to new PhotoMarkerShape
    const storedShape = storedSettings?.dragger?.photoMarkerShape;
    if (storedShape === "arrow") {
      // Legacy format - use the stored arrow type or default to thin_arrow
      this.activePhotoMarkerShape = Settings.arrowShape || "thin_arrow";
    } else if (
      storedShape === "circle" ||
      storedShape === "thin_arrow" ||
      storedShape === "large_arrow"
    ) {
      this.activePhotoMarkerShape = storedShape;
    } else {
      this.activePhotoMarkerShape = "circle";
    }
    // Track arrow type separately for legacy compatibility
    if (
      this.activePhotoMarkerShape === "thin_arrow" ||
      this.activePhotoMarkerShape === "large_arrow"
    ) {
      this.activeArrowType = this.activePhotoMarkerShape;
    }
    this.origin = storedSettings?.dragger?.origin || [0, 0, 0];
    if (storedSettings?.dragger?.label != undefined) {
      this.label = storedSettings.dragger.label;
    } else {
      this.label = true;
    }
    if (storedSettings?.dragger?.closeContour != undefined) {
      this.closeContour = storedSettings.dragger.closeContour;
    }
    if (storedSettings?.dragger?.sequentialPointMode != undefined) {
      this.sequentialPointMode = storedSettings.dragger.sequentialPointMode;
    }
    if (
      storedSettings?.dragger?.smoothen != undefined &&
      SMOOTHEN_VALUES.has(storedSettings.dragger.smoothen)
    ) {
      this.smoothen = storedSettings.dragger.smoothen;
    }
    // Migration: smoothenEnabled is replaced by smoothen === 0
    if (storedSettings?.dragger?.smoothenEnabled === false) {
      this.smoothen = 0;
    }
    if (storedSettings?.dragger?.smoothenEnabled != undefined) {
      _.unset(this.storedSettings, "dragger.smoothenEnabled");
    }
    if (
      storedSettings?.dragger?.handlesSize != undefined &&
      HANDLES_SCALE_VALUES.has(storedSettings.dragger.handlesSize)
    ) {
      this.handlesScale = storedSettings.dragger.handlesSize;
    }
    if (
      storedSettings?.dragger?.eraserSize != undefined &&
      ERASER_SIZE_VALUES.has(storedSettings.dragger.eraserSize)
    ) {
      this.eraserSize = storedSettings.dragger.eraserSize;
    }
    if (storedSettings?.dragger?.additiveSelection != undefined) {
      this.additiveSelection = storedSettings.dragger.additiveSelection;
    }
    if (storedSettings?.dragger?.isLwActive != undefined) {
      this.isLwActive = storedSettings.dragger.isLwActive;
    }
    if (storedSettings?.dragger?.lockNativeElements != undefined) {
      this.lockNativeElements = storedSettings.dragger.lockNativeElements;
    }
    if (storedSettings?.dragger?.rulerVisible != undefined) {
      this.rulerVisible = storedSettings.dragger.rulerVisible;
    }
    if (storedSettings?.dragger?.rulerAnchor != undefined) {
      this.rulerAnchor = storedSettings.dragger.rulerAnchor;
    }
    if (storedSettings?.snap) {
      const snapSettings = storedSettings?.snap;
      this.orthoMode = snapSettings.isOrthoActive;
      if (snapSettings.polar.isActive) {
        this.polarMode = snapSettings.polar.isActive;

        // Handle polar mode updates at init
        const possibleValues = Object.values(PolarModes);
        if (
          possibleValues.some(
            (value) => value === snapSettings.polar.polarAngle,
          )
        ) {
          this.polarAngleMode = snapSettings.polar.polarAngle as PolarModes;
        } else {
          this.polarAngleMode = PolarModes.polar45;
        }
      }

      this.objectSnapEnabled = snapSettings.isObjectSnapActive;

      if (snapSettings.objectSnapModes) {
        this.objectSnapModes = new Set(
          snapSettings.objectSnapModes.filter((m): m is ObjectSnapMode =>
            OBJECT_SNAP_MODE_ORDER.includes(m as ObjectSnapMode),
          ),
        );
      } else if (snapSettings.isObjectSnapActive) {
        // Migration: pre-feature users had object snap on with the old
        // hardcoded bundle and no persisted per-mode set - preserve their
        // existing behavior instead of silently going snap-less.
        this.objectSnapModes = new Set(DEFAULT_OBJECT_SNAP_MODES);
      }
    }
  },
  onDrawingOpen(options: { skipLineweight?: boolean } = {}) {
    if (!Facade.isInitialized) return;
    // Replaying stored preferences goes through the instrumented setters below;
    // suppress tracking so opening a drawing is not mistaken for user activity.
    withRestoreSuppressed(() => this.restoreOnDrawingOpen(options));
  },
  restoreOnDrawingOpen(options: { skipLineweight?: boolean } = {}) {
    LoadingTimer.step("DraggerMenuState.onDrawingOpen");

    // Activate the stored dragger
    if (this.activeDragger.name) {
      Facade.tools.activate(this.activeDragger.name);
    }

    // Set ruler origin, thickness and visibility
    Facade.rulers.origin = { x: this.origin[0], y: this.origin[1] };
    Facade.rulers.setSize(toRulerSize(this.storedSettings?.dragger?.rulerSize));
    if (this.rulerVisible) {
      Facade.rulers.show();
    } else {
      Facade.rulers.hide();
    }

    // Restore persisted WCS anchor positions and subscribe to future changes
    if (this.rulerAnchor !== null) {
      Facade.rulers.setAnchors(this.rulerAnchor.hY, this.rulerAnchor.vX);
    }
    Facade.rulers.onAnchorChanged = (hY, vX) => {
      this.rulerAnchor = { hY, vX };
      this.storedSettings = _.set(
        this.storedSettings || {},
        "dragger.rulerAnchor",
        this.rulerAnchor,
      );
      CanvasSettingsState.updateAndPersist();
    };

    // Set auto-close for freehand drawing
    Facade.autoClose.setEnabled(this.closeContour);

    // Set two-point sequential placement mode for Rectangle/Ellipse/Line
    Facade.tools.setTwoPointMode(this.sequentialPointMode);

    // Ensure photo marker shape is valid for the current legend
    this.ensureValidPhotoMarkerShape();

    // Set photo marker configuration
    Facade.photoMarkers.activeShape = toRaw(this.activePhotoMarkerShape);

    // Derive photo marker mode from active shape
    this.activePhotoMarkerMode =
      this.activePhotoMarkerShape === "text_only" ? "entity" : "insert";
    Facade.photoMarkers.mode = this.activePhotoMarkerMode;

    // Set photo marker attribute display (number vs date/time) from the legend
    this.syncPhotoMarkerAttributeConfig();

    // Set smoothen, handle size and eraser aperture
    this.setSmoothen(this.smoothen);
    this.setHandlesScale(this.handlesScale);
    this.setEraserSize(this.eraserSize);

    // Set additive selection mode
    this.setAdditiveSelection(this.additiveSelection);

    // Set lineweight display (can be skipped for deferred loading)
    if (!options.skipLineweight) {
      LoadingTimer.step("displayLineWeight start");
      Facade.displayLineWeight = this.isLwActive;
      LoadingTimer.step("displayLineWeight done");
    }

    // Sync label enabled state
    Facade.labelEnabled = this.label;

    // Enable snap modes if needed
    Facade.snap.setObjectSnap(this.objectSnapEnabled);
    Facade.snap.setSnapModes(new Set(this.objectSnapModes));

    Facade.snap.setOrtho(this.orthoMode);

    Facade.snap.setPolar(this.polarMode);
    Facade.snap.setPolarAngleMode(this.polarAngleMode);

    LoadingTimer.step("DraggerMenuState done");
  },
  /**
   * Apply lineweight display setting.
   * Called separately after initial loading to avoid blocking render.
   */
  applyLineweight() {
    if (!Facade.isInitialized) return;
    Facade.displayLineWeight = this.isLwActive;
  },
  setActiveDragger(dragger: DraggerConfig, source: ToolSource = "toolbar") {
    // Every tool selection in the UI funnels through here. Re-clicking the
    // active tool and clearing the tool are not usage, so they are not tracked.
    if (dragger.name && dragger.name !== this.activeDragger.name) {
      trackTool(dragger.name, source);
    }

    if (Facade.isInitialized) {
      Facade.tools.activate(dragger.name);
      Facade.selection.clear();
    }

    const isChangingToPhoto =
      dragger.name === DraggerType.PhotoMarker &&
      this.activeDragger.name !== DraggerType.PhotoMarker;
    if (isChangingToPhoto) {
      NoteMenuState.setActiveNoteConfig(
        NoteMenuState.getPhotoNoteConfig(),
        true,
      );
    }
    const isLeavingPhoto =
      this.activeDragger.name === DraggerType.PhotoMarker &&
      dragger.name !== DraggerType.PhotoMarker;
    if (isLeavingPhoto) {
      if (NoteMenuState.previousNote) {
        NoteMenuState.setActiveNoteConfig(NoteMenuState.previousNote);
      } else {
        Logger.warn(
          `DraggerMenuState : user is leaving photo but no previous defined`,
        );
      }
    }

    this.activeDragger = dragger;
    this.storedSettings = _.set(
      this.storedSettings || {},
      "dragger.name",
      this.activeDragger.name,
    );
    CanvasSettingsState.updateAndPersist();
  },
  setActiveText(text: string) {
    if (Facade.isInitialized) {
      Facade.text.setContent(toRaw(text));
    }
  },
  persistPhotoMarker() {
    if (Facade.isInitialized) {
      Facade.photoMarkers.activeShape = toRaw(this.activePhotoMarkerShape);
      this.syncPhotoMarkerAttributeConfig();
    }
  },

  persistOrigin(coord: [number, number, number]) {
    this.storedSettings = _.set(
      this.storedSettings || {},
      "dragger.origin",
      coord,
    );
    CanvasSettingsState.updateAndPersist();
  },
  toggleCloseContour() {
    this.closeContour = !this.closeContour;
    trackAssist("close_contour", this.closeContour);
    if (Facade.isInitialized) {
      Facade.autoClose.setEnabled(toRaw(this.closeContour));
    }
    this.storedSettings = _.set(
      this.storedSettings || {},
      "dragger.closeContour",
      this.closeContour,
    );
    CanvasSettingsState.updateAndPersist();
  },
  toggleSequentialPointMode() {
    this.sequentialPointMode = !this.sequentialPointMode;
    trackAssist("two_point_mode", this.sequentialPointMode);
    if (Facade.isInitialized) {
      Facade.tools.setTwoPointMode(toRaw(this.sequentialPointMode));
    }
    this.storedSettings = _.set(
      this.storedSettings || {},
      "dragger.sequentialPointMode",
      this.sequentialPointMode,
    );
    CanvasSettingsState.updateAndPersist();
  },
  setSmoothen(value: number) {
    this.smoothen = value;
    // Slider-driven: debounced so a drag counts once, with the final value.
    // Guarded at SCHEDULE time: the debounced fire runs after the restore
    // suppression has lifted, so onDrawingOpen replays would leak through.
    if (!isRestoringActive()) {
      trackDebounced("smoothen", () =>
        trackTab("settings", "smoothen_changed", { value }),
      );
    }
    // A value of 0 maps to a null epsilon, which disables smoothing
    const smoothenValue =
      SMOOTHEN_VALUES.get(value) ?? SMOOTHEN_VALUES.get(defaultSmoothenIndex)!;
    if (Facade.isInitialized) {
      Facade.smoothen.set(smoothenValue);
    }
    this.storedSettings = _.set(
      this.storedSettings || {},
      "dragger.smoothen",
      this.smoothen,
    );
    CanvasSettingsState.updateAndPersist();
  },
  setHandlesScale(value: number) {
    this.handlesScale = value;
    if (!isRestoringActive()) {
      trackDebounced("handles_size", () =>
        trackTab("settings", "handles_size_changed", { value }),
      );
    }
    const handlesScale = handlesScaleFactor(value);
    if (Facade.isInitialized) {
      Facade.selection.handleScale = handlesScale; // Normalize to scale factor
      Facade.rulers.setHandleScale(handlesScale);
    }
    this.storedSettings = _.set(
      this.storedSettings || {},
      "dragger.handlesSize",
      this.handlesScale,
    );
    CanvasSettingsState.updateAndPersist();
  },
  setEraserSize(value: number) {
    this.eraserSize = value;
    // Checked when scheduling, not when firing (cf. isRestoringActive): the
    // replay at drawing open used to land 1.5 s later as a phantom change.
    if (!isRestoringActive()) {
      trackDebounced("eraser_size", () => trackAssist("eraser_size", value));
    }
    eraserConfig.aperturePx =
      ERASER_SIZE_VALUES.get(value) ??
      ERASER_SIZE_VALUES.get(defaultEraserSizeIndex)!;
    this.storedSettings = _.set(
      this.storedSettings || {},
      "dragger.eraserSize",
      this.eraserSize,
    );
    CanvasSettingsState.updateAndPersist();
  },
  setAdditiveSelection(enabled: boolean) {
    this.additiveSelection = enabled;
    trackAssist("additive_selection", enabled);
    selectConfig.additive = enabled;
    this.storedSettings = _.set(
      this.storedSettings || {},
      "dragger.additiveSelection",
      this.additiveSelection,
    );
    CanvasSettingsState.updateAndPersist();
  },
  toggleLabel() {
    this.label = !this.label;
    trackAssist("label", this.label);
    this.storedSettings = _.set(
      this.storedSettings || {},
      "dragger.label",
      this.label,
    );
    if (Facade.isInitialized) {
      Facade.labelEnabled = this.label;
    }
    CanvasSettingsState.updateAndPersist();
  },
  toggleLineWeight() {
    this.isLwActive = !this.isLwActive;
    trackTab("settings", "lineweight_toggled", { value: this.isLwActive });
    this.storedSettings = _.set(
      this.storedSettings || {},
      "dragger.isLwActive",
      this.isLwActive,
    );
    CanvasSettingsState.updateAndPersist();
    Logger.info(`DraggerMenuState: lineweightScale set to ${this.isLwActive}`);

    // Enable/disable lineweight display and refresh the view
    Facade.displayLineWeight = this.isLwActive;
  },
  toggleLockNativeElements() {
    this.lockNativeElements = !this.lockNativeElements;
    this.storedSettings = _.set(
      this.storedSettings || {},
      "dragger.lockNativeElements",
      this.lockNativeElements,
    );
    CanvasSettingsState.updateAndPersist();
    // Lock native elements is a selection mode setting
  },
  setOrthoMode(isOrtho: boolean) {
    this.orthoMode = isOrtho;
    trackAssist("ortho", isOrtho);
    if (Facade.isInitialized) {
      Facade.snap.setOrtho(toRaw(this.orthoMode));
    }
    this.storeSnap();
    CanvasSettingsState.updateAndPersist();
  },
  setPolarMode(isPolar: boolean) {
    this.polarMode = isPolar;
    trackAssist("polar", isPolar);
    if (Facade.isInitialized) {
      Facade.snap.setPolar(toRaw(this.polarMode));
    }
    this.storeSnap();
  },
  setPolarAngleMode(mode: PolarAngleMode) {
    this.polarAngleMode = mode;
    trackAssist("polar_angle", mode);
    if (Facade.isInitialized) {
      Facade.snap.setPolarAngleMode(toRaw(this.polarAngleMode));
    }
    this.storeSnap();
  },
  setObjectSnapEnabled(isEnabled: boolean) {
    this.objectSnapEnabled = isEnabled;
    trackAssist("object_snap", isEnabled);
    if (Facade.isInitialized) {
      Facade.snap.setObjectSnap(toRaw(this.objectSnapEnabled));
    }
    this.storeSnap();
  },
  /**
   * Interrupteur général (moitié gauche du bouton scindé). Couper l'accrochage
   * garde les modes choisis : les rallumer, c'est retrouver son réglage.
   */
  toggleObjectSnap() {
    if (this.objectSnapEnabled) {
      this.setObjectSnapEnabled(false);
    } else {
      if (this.objectSnapModes.size === 0) {
        this.objectSnapModes = new Set(DEFAULT_OBJECT_SNAP_MODES);
        if (Facade.isInitialized) {
          Facade.snap.setSnapModes(new Set(this.objectSnapModes));
        }
      }
      this.setObjectSnapEnabled(true);
    }
    CanvasSettingsState.updateAndPersist();
  },
  toggleObjectSnapMode(mode: ObjectSnapMode) {
    if (this.objectSnapModes.has(mode)) {
      this.objectSnapModes.delete(mode);
      if (Facade.isInitialized) {
        Facade.snap.disableSnapMode(mode);
      }
    } else {
      this.objectSnapModes.add(mode);
      if (Facade.isInitialized) {
        Facade.snap.enableSnapMode(mode);
      }
    }

    // Master switch tracks whether any mode is enabled
    const shouldBeEnabled = this.objectSnapModes.size > 0;
    if (shouldBeEnabled !== this.objectSnapEnabled) {
      this.objectSnapEnabled = shouldBeEnabled;
      if (Facade.isInitialized) {
        Facade.snap.setObjectSnap(shouldBeEnabled);
      }
    }

    this.storeSnap();
    CanvasSettingsState.updateAndPersist();
  },
  isObjectSnapModeEnabled(mode: ObjectSnapMode): boolean {
    return this.objectSnapModes.has(mode);
  },
  setPhotoMarkerShapeFromFavorite(shape: PhotoMarkerShape) {
    if (shape !== this.activePhotoMarkerShape) {
      trackShapeChange("photo_marker_shape_changed", { shape });
    }
    this.activePhotoMarkerShape = shape;
    // Track arrow type if it's an arrow
    if (shape === "thin_arrow" || shape === "large_arrow") {
      this.activeArrowType = shape;
      // Also update the legacy Settings.arrowShape for compatibility
      Settings.setPhotoMarkerShape(shape);
    }

    this.storedSettings = _.set(
      this.storedSettings || {},
      "dragger.photoMarkerShape",
      this.activePhotoMarkerShape,
    );

    CanvasSettingsState.updateAndPersist();
    if (Facade.isInitialized) {
      Facade.photoMarkers.activeShape = toRaw(this.activePhotoMarkerShape);
      Facade.photoMarkers.mode =
        this.activePhotoMarkerShape === "text_only" ? "entity" : "insert";
    }
  },
  getFavoritePhotoMarkers(): PhotoMarkerShape[] {
    return (
      CanvasSettingsState.activeLegend?.settings?.favorite_photo_markers ??
      DEFAULT_LEGEND_SETTINGS.favorite_photo_markers
    );
  },
  getPhotoMarkerAttribute(): PhotoMarkerAttribute {
    return (
      CanvasSettingsState.activeLegend?.settings?.photo_marker_attribute ??
      DEFAULT_LEGEND_SETTINGS.photo_marker_attribute
    );
  },
  getPhotoMarkerDateFormat(): string {
    return (
      CanvasSettingsState.activeLegend?.settings?.photo_marker_date_format ??
      DEFAULT_LEGEND_SETTINGS.photo_marker_date_format
    );
  },
  syncPhotoMarkerAttributeConfig() {
    if (!Facade.isInitialized) return;
    Facade.photoMarkers.attributeMode = this.getPhotoMarkerAttribute();
    Facade.photoMarkers.dateFormat = this.getPhotoMarkerDateFormat();
  },
  getPhotoMarkerMode(): PhotoMarkerMode {
    return this.activePhotoMarkerShape === "text_only" ? "entity" : "insert";
  },
  setPhotoMarkerMode(mode: PhotoMarkerMode) {
    this.activePhotoMarkerMode = mode;
    if (Facade.isInitialized) {
      Facade.photoMarkers.mode = mode;
    }
  },
  ensureValidPhotoMarkerShape() {
    const favorites = this.getFavoritePhotoMarkers();
    if (!favorites.includes(this.activePhotoMarkerShape)) {
      // Current shape is not in favorites, switch to first favorite
      this.activePhotoMarkerShape = favorites[0];
      if (
        this.activePhotoMarkerShape === "thin_arrow" ||
        this.activePhotoMarkerShape === "large_arrow"
      ) {
        this.activeArrowType = this.activePhotoMarkerShape;
      }
      // Persist the change
      this.storedSettings = _.set(
        this.storedSettings || {},
        "dragger.photoMarkerShape",
        this.activePhotoMarkerShape,
      );
      CanvasSettingsState.updateAndPersist();
    }
  },
  getPhotoLayer() {
    const photoNote = NoteMenuState.noteConfigs.find(
      (note) => note.type === "photo",
    );
    const photoLayer = CanvasSettingsState.activeLegend.layer_configs.find(
      (layer) => layer.id === photoNote?.props.layerId,
    );
    return photoLayer;
  },
  getLayer(layerName: string) {
    return CanvasSettingsState.activeLegend.layer_configs.find(
      (layer) => layer.name === layerName,
    );
  },
  getLayerById(layerId: string) {
    return CanvasSettingsState.activeLegend.layer_configs.find(
      (layer) => layer.id === layerId,
    );
  },
  storeSnap() {
    this.storedSettings = _.set(this.storedSettings || {}, "snap", {
      isOrthoActive: this.orthoMode,
      polar: {
        isActive: this.polarMode,
        polarAngle: this.polarAngleMode,
      },
      isObjectSnapActive: this.objectSnapEnabled,
      objectSnapModes: Array.from(this.objectSnapModes),
    });
  },
});
